import { create } from "zustand";
import {
  generateBlogContent,
  generateTitleSuggestions,
  generateMetaDescription,
  suggestImprovements,
} from "../lib/openai";

interface AIState {
  content: string;
  titleSuggestions: string[];
  metaDescription: string;
  improvements: string;
  loading: boolean;
  error: string | null;
  conversationHistory: string[];
  generateContent: (prompt: string) => Promise<void>;
  generateTitles: () => Promise<void>;
  generateMeta: () => Promise<void>;
  getImprovements: () => Promise<void>;
  setContent: (content: string) => void;
  clearError: () => void;
  addToHistory: (message: string) => void;
  clearHistory: () => void;
  clearContent: () => void;
}

export const useAIStore = create<AIState>((set, get) => ({
  content: "",
  titleSuggestions: [],
  metaDescription: "",
  improvements: "",
  loading: false,
  error: null,
  conversationHistory: [],

  generateContent: async (prompt: string) => {
    try {
      set({ loading: true, error: null });
      const { conversationHistory } = get();
      const context =
        conversationHistory.length > 0
          ? conversationHistory.join("\n")
          : undefined;

      // Generate content first
      const content = await generateBlogContent(prompt, context);
      set({ content });
      get().addToHistory(prompt);

      // Then automatically get improvements
      try {
        const improvements = await suggestImprovements(content);
        set({ improvements });
      } catch (improvementError) {
        console.error("Error getting improvements:", improvementError);
        // Don't set error state here since the main content generation succeeded
      }

      set({ loading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : "Failed to generate content",
        loading: false,
      });
    }
  },

  generateTitles: async () => {
    try {
      set({ loading: true, error: null });
      const { content } = get();
      if (!content) throw new Error("No content available to generate titles");
      const suggestions = await generateTitleSuggestions(content);
      set({ titleSuggestions: suggestions, loading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : "Failed to generate titles",
        loading: false,
      });
    }
  },

  generateMeta: async () => {
    try {
      set({ loading: true, error: null });
      const { content } = get();
      if (!content)
        throw new Error("No content available to generate meta description");
      const description = await generateMetaDescription(content);
      set({ metaDescription: description, loading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate meta description",
        loading: false,
      });
    }
  },

  getImprovements: async () => {
    try {
      set({ loading: true, error: null });
      const { content } = get();
      if (!content) throw new Error("No content available to analyze");
      const suggestions = await suggestImprovements(content);
      set({ improvements: suggestions, loading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate improvements",
        loading: false,
      });
    }
  },

  setContent: (content: string) => set({ content }),
  clearError: () => set({ error: null }),
  addToHistory: (message: string) =>
    set((state) => ({
      conversationHistory: [...state.conversationHistory, message],
    })),
  clearHistory: () => set({ conversationHistory: [] }),
  clearContent: () =>
    set({
      content: "",
      titleSuggestions: [],
      metaDescription: "",
      improvements: "",
    }),
}));
